import { computed, makeObservable } from 'mobx';

import { getAllConnections, type ConnectionIdentity } from 'api/connections/endpoints/getAllConnections';
import type { ConnectionReference } from 'edit/reference/EditConnectionReference';
import { answerOf, MobxQuery } from 'mobx/MobxQuery';

// A REST node's `config.connection` is a string that holds EITHER a connection id (what this editor
// stores) OR, in an older config, a connection name. Nothing records which. The backend guesses at
// dispatch (nap-backend `dispatchRest.js`, `getConnectionDetails`), and the editor has to guess the
// same way, or it shows one connection while the node runs against another.
//
// The rules below mirror that function. Read it before changing them, and keep these pitfalls in mind:
//
// - The lookup covers EVERY connection, not only REST ones. Both backend lookups ignore the type: a
//   stored id resolves even if that connection has since become an SSH connection, and the name
//   fallback matches a connection of any type. Loading only REST connections (what the picker offers)
//   would turn such an id miss into a name match the runtime never makes.
// - "Is it an id?" is `Number(ref)` being a positive integer, not "is it all digits". So `"012"`,
//   `"12.0"`, `"1e1"` and `"0x1A"` are ids 12, 12, 10 and 26, while `"0"` is a NAME. There is no
//   connection 0 (ids are an identity column starting at 1), and the backend never looks one up.
// - An id that matches no connection is not the end: the backend then tries it as a name, so a stored
//   `"42"` with no connection 42 runs against a connection NAMED "42", if there is one.
// - Name matching is trimmed and case-insensitive on both sides. Names are not unique, and the backend
//   picks an arbitrary row among duplicates (`LIMIT 1`, no `ORDER BY`). That cannot be mirrored; the
//   lowest id is taken here.
// - The transport exporter guesses differently again (`/^\d+$/`), so `"0"` runs by name but fails to
//   export. Not ours to fix; nap-backend#1916 migrates legacy names to ids and removes the guessing,
//   after which all of this can go.
// - What the editor shows is what THIS DEFINITION resolves to. A submitted instance's `node_data` can
//   override `connection` at dispatch, and the editor does not see that.

/** Postgres `TRIM` strips spaces only, not other whitespace, and the backend lowercases both sides. */
const normalizeName = (name: string): string => name.replace(/^ +| +$/g, '').toLowerCase();

/** The id the backend would look up for `ref`, or null when it goes straight to a name match. */
const idOf = (ref: string): number | null => {
  const n = Number(ref);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const asReference = (c: ConnectionIdentity): ConnectionReference => ({
  connection_id: c.connection_id,
  connection_name: c.connection_name,
  connection_type: c.connection_type,
});

/**
 * A reference that shows `ref` as-is and matches no option. Its key is `NaN`, which equals nothing,
 * not even itself, so the picker never mistakes it for a real connection. Its label is set, so the
 * picker does not try to look it up.
 */
const standIn = (ref: string): ConnectionReference => ({ connection_id: Number.NaN, connection_name: ref, connection_type: '' });

/**
 * The picker value for a stored `config.connection`, resolved the way REST dispatch resolves it.
 *
 * `connections` is every connection there is, or null when that list could not be loaded. Without the
 * list this is best effort: an id becomes an id-only reference, which the picker labels by looking the
 * id up itself (showing `Connection #N` if that fails), and anything else is shown as-is. That gets
 * the common cases right and only mislabels the rare ones: an id with no connection behind it that
 * the backend would re-try as a name.
 *
 * Every connection-backed result carries its label, so the picker does no lookup of its own and the
 * connection it shows is the one this function chose.
 */
export const toReference = (stored: unknown, connections: readonly ConnectionIdentity[] | null): ConnectionReference | null => {
  // The backend trims with JS `trim()` before either lookup, so all whitespace goes here.
  const ref = stored == null ? '' : String(stored).trim();
  if (ref === '') return null;

  const id = idOf(ref);
  if (connections === null) {
    return id !== null ? { connection_id: id, connection_name: '', connection_type: '' } : standIn(ref);
  }

  if (id !== null) {
    const byId = connections.find((c) => c.connection_id === id);
    if (byId !== undefined) return asReference(byId);
  }

  const name = normalizeName(ref);
  let byName: ConnectionIdentity | undefined;
  for (const c of connections) {
    if (normalizeName(c.connection_name) === name && (byName === undefined || c.connection_id < byName.connection_id)) byName = c;
  }
  // Nothing matches: the node fails at dispatch ("REST connection not found"). Show what is stored.
  return byName !== undefined ? asReference(byName) : standIn(ref);
};

/**
 * What the editor knows about the connection list:
 * - `loading`: not there yet, so no reference can be resolved.
 * - `loaded`: every connection.
 * - `unavailable`: the load failed, so resolve best effort without it.
 */
export type ConnectionLookup =
  { readonly kind: 'loading' } | { readonly kind: 'loaded'; readonly connections: readonly ConnectionIdentity[] } | { readonly kind: 'unavailable' };

// Several query states collapse onto one lookup (`uninitialized` and `loading` are both `loading`), so
// equality is by what the lookup says: the same kind and, when loaded, the same list.
const sameLookup = (a: ConnectionLookup, b: ConnectionLookup): boolean => {
  if (a.kind === 'loaded' && b.kind === 'loaded') return a.connections === b.connections;
  return a.kind === b.kind;
};

/**
 * Loads the full connection list that {@link toReference} resolves a stored REST connection against.
 *
 * One read with no parameters, as a {@link MobxQuery} so it starts when the editor first reads `lookup`
 * and tears itself down when the editor goes away. A failure is not an error to show: the editor falls
 * back to a best-effort reference and stays editable, because the list only sharpens the label of an
 * odd legacy value, and a node definition must stay editable without it.
 */
export class RestConnectionModel {
  private readonly _connections = new MobxQuery<null, readonly ConnectionIdentity[]>({
    params: () => null,
    fetch: () => getAllConnections(),
    debounceMs: 0,
  });

  constructor() {
    makeObservable(this, { lookup: computed({ equals: sameLookup }) });
  }

  /**
   * Reading it in a reaction is what starts the load. `computed`, so the identity holds while nothing
   * changes: the editor memoizes the picker's value on it, and the picker re-syncs on every new value.
   */
  get lookup(): ConnectionLookup {
    const value = this._connections.value;
    const answer = answerOf(value);
    if (answer !== null) return { kind: 'loaded', connections: answer.value };
    return value.kind === 'error' ? { kind: 'unavailable' } : { kind: 'loading' };
  }

  /** For tests; see `MobxQuery.dispose`. */
  dispose = (): void => {
    this._connections.dispose();
  };
}
